import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import Request from '../../../../../../../helpers/request'
import * as url from '../../../../../../../helpers/url_helper'
import PaginationBar, { paginate, usePersistedPage } from '../../../../../../../components/Common/PaginationBar'
import { controlAddressForSwitch } from '../../../../../../../helpers/mesh_address'

interface Props {
    rooms: any[]
    relays: any[]
    switches: any[]
    devices: any[]
    pcbs: any[]
    placeId: string
    userId: string
    [key: string]: any
}

const renderOnline = (sw: any) => {
    if (sw.switchIsOnline === true) return <span style={{ color: '#16A34A' }}>● Online</span>
    if (sw.switchIsOnline === false) return <span style={{ color: '#9CA3AF' }}>○ Offline</span>
    return <span style={{ color: '#858D9D' }}>--</span>
}

// Plug-type switches (98 = Universal Plug, 99 = Indian Plug) are passive
// receptacles — no toggle / rotate / press gesture, so they don't belong
// in the Manual Controls view. They still appear in Appliances as loads.
const PLUG_TYPES = new Set([98, 99])
const isManualControl = (sw: any) => !PLUG_TYPES.has(Number(sw.type))

const TH = ({ children }: { children: React.ReactNode }) => (
    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 13, color: '#858D9D', fontWeight: 500, background: '#F9FAFB' }}>
        {children}
    </th>
)

const TD = ({ children }: { children: React.ReactNode }) => (
    <td style={{ padding: '12px 16px', fontSize: 14, color: '#344054', borderTop: '1px solid #E4E7EC' }}>
        {children}
    </td>
)

const PlaceManualControls = ({ rooms, relays, switches, devices, pcbs, placeId, userId }: Props) => {
    const navigate = useNavigate()
    const [busySwId, setBusySwId] = useState<string | null>(null)
    const [appliancesTarget, setAppliancesTarget] = useState<any>(null)
    const [page, setPage] = usePersistedPage(`place-manualcontrols-${placeId}`)
    const placeRooms = (rooms || []).filter(r => r.placeId === placeId)
    const placeRelays = (relays || []).filter(r => r.placeId === placeId)
    const roomMap = Object.fromEntries(placeRooms.map(r => [r.id, r.name]))
    const relayMap = Object.fromEntries(placeRelays.map(r => [r.id, r.name]))
    const pcbById: Record<string, any> = Object.fromEntries((pcbs || []).map((p: any) => [p.id, p]))
    const placeSwitches = (switches || []).filter(s => s.placeId === placeId && isManualControl(s))
    const { paged: pagedSwitches, totalPages, safePage } = paginate(placeSwitches, page)

    // device.id → device, for local lookup (no backend round-trip needed).
    const deviceById: Record<string, any> = Object.fromEntries(
        (devices || []).map((d: any) => [d.id, d]),
    )

    const pingSwitch = (sw: any) => {
        // Ping the host PCB (same path the Modules tab uses) via PING_PCB.
        if (!sw.pcbId) {
            toast.error(`No host module found for ${sw.name || sw.id}`)
            return
        }
        setBusySwId(sw.id)
        Request.post(url.PING_PCB, { moduleId: sw.pcbId })
            .then(() => toast.success(`Ping sent to ${sw.name || sw.id}`))
            .catch((err: any) => toast.error(err?.response?.data?.msg || 'Ping failed'))
            .finally(() => setBusySwId(null))
    }

    // Default (no-group) association: switch.deviceId points to a single device.
    // When grouping lands (joint dev), expand this to a list via switch_devices.
    const appliancesForSwitch = (sw: any) => {
        if (!sw?.deviceId) return []
        const d = deviceById[sw.deviceId]
        return d ? [d] : []
    }

    return (
        <div>
        <div style={{ background: '#fff', border: '1px solid #E4E7EC', borderRadius: 8, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <TH>Name</TH>
                        <TH>Panel</TH>
                        <TH>Type</TH>
                        <TH>Room</TH>
                        <TH>Online</TH>
                        <TH>Unicast</TH>
                        <TH>Actions</TH>
                    </tr>
                </thead>
                <tbody>
                    {placeSwitches.length === 0 && (
                        <tr>
                            <td colSpan={7} style={{ padding: 20, textAlign: 'center', color: '#858D9D', fontSize: 14 }}>
                                No manual controls found for this place
                            </td>
                        </tr>
                    )}
                    {pagedSwitches.map((sw, idx) => (
                        <tr key={sw.id || idx}>
                            <TD>{sw.name || sw.Name || '--'}</TD>
                            <TD>{relayMap[sw.relayId] || '--'}</TD>
                            <TD>{sw.type || '--'}</TD>
                            <TD>
                                {sw.roomId ? (
                                    <button
                                        onClick={() => navigate(`/users/profile/${userId}/places/${placeId}/rooms/${sw.roomId}`)}
                                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#1a73e8', fontSize: 14, textDecoration: 'underline' }}
                                    >
                                        {roomMap[sw.roomId] || sw.roomId}
                                    </button>
                                ) : '--'}
                            </TD>
                            <TD>{renderOnline(sw)}</TD>
                            <TD>{(() => {
                                const addr = controlAddressForSwitch(sw, pcbById[sw.pcbId])
                                return addr != null ? addr : '--'
                            })()}</TD>
                            <TD>
                                <button
                                    data-testid={`ping-sw-${sw.id}`}
                                    onClick={() => pingSwitch(sw)}
                                    disabled={busySwId === sw.id}
                                    style={{
                                        padding: '4px 10px', fontSize: 12, fontWeight: 500,
                                        background: '#fff', color: '#1a73e8', border: '1px solid #1a73e8',
                                        borderRadius: 4, cursor: busySwId === sw.id ? 'not-allowed' : 'pointer',
                                        opacity: busySwId === sw.id ? 0.5 : 1,
                                        marginRight: 4,
                                    }}
                                >
                                    Ping
                                </button>
                                <button
                                    onClick={() => setAppliancesTarget(sw)}
                                    title="Show appliances linked to this control"
                                    style={{
                                        padding: '4px 10px', fontSize: 12, fontWeight: 500,
                                        background: '#fff', color: '#107569', border: '1px solid #107569',
                                        borderRadius: 4, cursor: 'pointer',
                                        marginRight: 4,
                                    }}
                                >
                                    Appliances
                                </button>
                                <button
                                    onClick={() => navigate(`/users/profile/${userId}/places/${placeId}/logs`)}
                                    title="Open the Logs tab for this place"
                                    style={{
                                        padding: '4px 10px', fontSize: 12, fontWeight: 500,
                                        background: '#fff', color: '#475467', border: '1px solid #475467',
                                        borderRadius: 4, cursor: 'pointer',
                                    }}
                                >
                                    Logs
                                </button>
                            </TD>
                        </tr>
                    ))}
                </tbody>
            </table>
            <PaginationBar
                page={safePage}
                totalPages={totalPages}
                total={placeSwitches.length}
                onPage={setPage}
            />
        </div>

        {appliancesTarget && (() => {
            const linked = appliancesForSwitch(appliancesTarget)
            return (
                <div
                    data-testid="appliances-dialog"
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
                    }}
                    onClick={() => setAppliancesTarget(null)}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: '#fff', borderRadius: 12, padding: 24,
                            maxWidth: 640, width: '90%', maxHeight: '80vh', overflow: 'auto',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                        }}
                    >
                        <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                            Appliances linked to <code style={{ fontSize: 13 }}>{appliancesTarget.name || appliancesTarget.id}</code>
                        </h4>
                        {linked.length > 0 ? (
                            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
                                <thead>
                                    <tr>
                                        <TH>Name</TH>
                                        <TH>Room</TH>
                                    </tr>
                                </thead>
                                <tbody>
                                    {linked.map((d: any) => (
                                        <tr key={d.id}>
                                            <TD>{d.name || '--'}</TD>
                                            <TD>{roomMap[d.roomId] || '--'}</TD>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div style={{ padding: 20, textAlign: 'center', color: '#858D9D', fontSize: 14 }}>
                                No appliances linked to this control
                            </div>
                        )}
                        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setAppliancesTarget(null)}
                                style={{ padding: '8px 16px', fontSize: 14, background: '#fff', color: '#374151', border: '1px solid #D1D5DB', borderRadius: 6, cursor: 'pointer' }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )
        })()}
        </div>
    )
}

export default PlaceManualControls
